import React, { useState } from 'react';
import { LoadingState } from './LoadingState';

const PreloadedSpotifyEmbed = ({ trackId, trackName = 'Spotify Track' }) => {
  const [loaded, setLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleLoad = () => {
    setIsLoading(false);
    setLoaded(true);
  };

  const handleClick = () => {
    if (!loaded && !isLoading) {
      setIsLoading(true);
    }
  };

  // Map track IDs to their album art images
  const trackAlbumArt = {
    '0rt1nQOw34V0SXRAw5oOuD': '/album-art/ghost-in-plain-sight.jpg', // Ghost In Plain Sight
    '4Fujv6FSCx3EzSLyYO3zlz': '/album-art/venomous-crusade.jpg', // Venomous Crusade
    '4E83tT4l2rlPLID47YpwVy': '/album-art/from-the-inside.jpg', // From The Inside
    '15TLRRojTlqRKI3aT8MV0b': '/album-art/masked-reality.jpg', // Masked Reality
    '6ydc7efV09laNTLfuzVDPb': '/album-art/autumns-embrace.jpg', // Autumn's Embrace
    '2H2FKAtMcmfufnWgeYMg02': '/album-art/screen-lit-youth.jpg', // Screen-Lit Youth
    '6cExbnvow5Vsw8M5Wg3atS': '/album-art/ok-boomer.jpg' // OK Boomer
  };

  const fallbackSources = [
    trackAlbumArt[trackId] || '/artist_photo.png',
    '/artist_photo.png',
    `https://via.placeholder.com/300x300/1f2937/9ca3af?text=${encodeURIComponent(trackName)}`
  ];
  const [imgSrc, setImgSrc] = useState(fallbackSources[0]);
  const [fallbackIdx, setFallbackIdx] = useState(0);

  const handleImgError = () => {
    if (fallbackIdx < fallbackSources.length - 1) {
      setFallbackIdx(fallbackIdx + 1);
      setImgSrc(fallbackSources[fallbackIdx + 1]);
    }
  };

  return (
    <div className="h-[152px] bg-black rounded-lg overflow-hidden relative border border-gray-800 hover:border-gray-600 transition-colors shadow-lg">
      {!loaded && (
        <div className="absolute inset-0 cursor-pointer" onClick={handleClick}>
          {/* Album art as background */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${imgSrc})` }}
          >
            {/* Dark overlay for readability */}
            <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          </div>

          {/* Play button (Spotify green) */}
          <div className="absolute inset-0 flex items-center justify-center">
            <button className="bg-green-500 hover:bg-green-600 text-white w-16 h-16 rounded-full flex items-center justify-center transition-colors shadow-lg">
              <svg className="w-8 h-8 ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </button>
          </div>

          {/* Bottom info bar */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
            <h3 className="text-white font-bold text-lg mb-1 truncate">{trackName}</h3>
            <p className="text-gray-300 text-sm">Exlo</p>
          </div>

          {/* Spotify logo top right */}
          <div className="absolute top-4 right-4">
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.062 14.615a.625.625 0 01-.862.227c-2.36-1.44-5.328-1.766-8.828-.97a.625.625 0 11-.36-1.195c3.838-.87 7.105-.496 9.823 1.076a.625.625 0 01.227.862zm1.23-2.737a.781.781 0 01-1.076.285c-2.702-1.652-6.822-2.13-10.019-1.164a.781.781 0 11-.45-1.495c3.66-1.106 8.19-.568 11.26 1.298a.781.781 0 01.285 1.076zm.106-2.852C14.692 8.99 9.391 8.765 6.316 9.71a.937.937 0 11-.54-1.794c3.53-.946 9.403-.683 12.952 1.573a.937.937 0 11-.955 1.621z"/>
            </svg>
          </div>

          {/* Hidden img for fallback loading */}
          <img src={imgSrc} alt="album art" className="hidden" onError={handleImgError} />

          {/* Loading overlay */}
          {isLoading && (
            <div className="absolute inset-0 bg-black bg-opacity-90 flex items-center justify-center rounded-lg">
              <LoadingState message="Loading Spotify Player..." />
            </div>
          )}
        </div>
      )}

      {loaded && (
        <iframe
          title={`Spotify track ${trackId}`}
          src={`https://open.spotify.com/embed/track/${trackId}?utm_source=generator&theme=0`}
          width="100%"
          height="152"
          frameBorder="0"
          allowtransparency="true"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
          onLoad={handleLoad}
          className="absolute inset-0 rounded-lg"
        />
      )}

      {isLoading && !loaded && (
        <iframe
          title={`Spotify track ${trackId}`}
          src={`https://open.spotify.com/embed/track/${trackId}?utm_source=generator&theme=0`}
          width="100%"
          height="152"
          frameBorder="0"
          allowtransparency="true"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
          onLoad={handleLoad}
          className="absolute inset-0 rounded-lg"
          style={{ opacity: 0 }}
        />
      )}
    </div>
  );
};

const PreloadedYouTubeEmbed = ({ videoId, videoTitle = 'YouTube Video' }) => {
  const [loaded, setLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleLoad = () => {
    setIsLoading(false);
    setLoaded(true);
  };

  const handleClick = () => {
    if (!loaded && !isLoading) {
      setIsLoading(true);
    }
  };

  // Generate YouTube thumbnail URL
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  return (
    <div className="aspect-video bg-black rounded-lg overflow-hidden relative border border-gray-800 hover:border-gray-600 transition-colors">
      {!loaded && (
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={handleClick}
        >
          {/* YouTube thumbnail background */}
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${thumbnailUrl})` }}
          >
            {/* Dark overlay for better text readability */}
            <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          </div>
          
          {/* YouTube-like UI elements */}
          <div className="absolute top-2 right-2 bg-black bg-opacity-70 px-2 py-1 rounded text-white text-xs">
            {isLoading ? 'Loading...' : 'HD'}
          </div>
          
          {/* Play button */}
          <div className="absolute inset-0 flex items-center justify-center">
            <button className="bg-red-600 hover:bg-red-700 text-white w-16 h-16 rounded-full flex items-center justify-center transition-colors shadow-lg">
              <svg className="w-6 h-6 ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </button>
          </div>
          
          {/* Bottom info bar */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
            <h3 className="text-white font-bold text-lg mb-1">{videoTitle}</h3>
            <p className="text-gray-300 text-sm">Exlo</p>
          </div>
          
          {/* Loading overlay */}
          {isLoading && (
            <div className="absolute inset-0 bg-black bg-opacity-90 flex items-center justify-center">
              <LoadingState message="Loading YouTube Player..." />
            </div>
          )}
        </div>
      )}
      
      {loaded && (
        <iframe
          title={`YouTube video ${videoId}`}
          width="100%"
          height="100%"
          src={`https://www.youtube.com/embed/${videoId}`}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          onLoad={handleLoad}
          className="absolute inset-0"
        />
      )}
      
      {isLoading && !loaded && (
        <iframe
          title={`YouTube video ${videoId}`}
          width="100%"
          height="100%"
          src={`https://www.youtube.com/embed/${videoId}`}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          onLoad={handleLoad}
          className="absolute inset-0"
          style={{ opacity: 0 }}
        />
      )}
    </div>
  );
};

export { PreloadedSpotifyEmbed, PreloadedYouTubeEmbed };
